import { useCallback, useRef, useState } from 'react'
import {
  emptyStoriesState,
  IStoriesState,
  readStoriesState,
  STORIES_STORAGE_KEY,
  writeStoriesState,
} from './storiesState'
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect'

/**
 * Another stories block on the same page saved: the browser's 'storage' event
 * only reaches other tabs
 */
const SAME_PAGE_EVENT = 'r13-stories-saved'

interface ISavedDetail {
  from: object
  render: boolean
}

/**
 * The stored stories state. The server and the hydration draw a first visit
 * (the HTML matches); the stored state is drawn right after, before the
 * browser paints (a layout effect), so a page reached by a link inside the
 * site never shows the first visit's order. On a full load the server's row
 * is on screen until the page's code runs (on a phone, the page loader covers
 * it until then). `save` writes it; `render: false` keeps frequent writes (the clip's
 * time) from redrawing the rail.
 */
export const useStoriesState = () => {
  const [state, setState] = useState<IStoriesState>(emptyStoriesState)
  const [ready, setReady] = useState(false)
  const stateRef = useRef(state)
  // this block, to tell its own saves from another block's
  const self = useRef({})

  useIsomorphicLayoutEffect(() => {
    const load = (render: boolean) => {
      const stored = readStoriesState(Date.now())
      stateRef.current = stored
      if (render) {
        setState(stored)
      }
    }
    load(true)
    setReady(true)
    // another tab watched something: follow it
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORIES_STORAGE_KEY) {
        load(true)
      }
    }
    // another block here did: its save must not be written over by ours
    const onSaved = (e: Event) => {
      const detail = (e as CustomEvent<ISavedDetail>).detail
      if (detail && detail.from !== self.current) {
        load(detail.render)
      }
    }
    // a page brought back from the back/forward cache kept its old state, and
    // missed the 'storage' events meanwhile
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        load(true)
      }
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener(SAME_PAGE_EVENT, onSaved)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(SAME_PAGE_EVENT, onSaved)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [])

  const save = useCallback(
    (change: (s: IStoriesState) => IStoriesState, render = true) => {
      const next = change(stateRef.current)
      stateRef.current = next
      // with storage blocked each block keeps its own state for this visit
      if (writeStoriesState(next, Date.now())) {
        const detail: ISavedDetail = { from: self.current, render }
        window.dispatchEvent(new CustomEvent(SAME_PAGE_EVENT, { detail }))
      }
      if (render) {
        setState(next)
      }
    },
    []
  )

  return { state, stateRef, ready, save }
}
